import React, { useEffect, useRef, useState } from 'react';
import { NumericFormat as NumberFormat } from 'react-number-format';
import { LocalizedStrings } from '@src/utils/localizedStrings';

import { StringHelper } from '@src/services/helpers/string';
import { CollectionView } from 'common';
import { useQueryCollectionContentNumber } from '@src/queries/collections/useQueryCollectionContentNumber';
import { style, classes } from 'typestyle';
import { Loader } from '../../../../components/Loader';


interface Props {
  collection: CollectionView;
  onClick: () => void;
  full?: boolean;
  backgroudImageUrl?: string;
  className?: string;
}

export function CollectionThumbCircle(props: Props) {
  const [visible, setVisible] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const { data: contentNumber } = useQueryCollectionContentNumber(props.collection._id, {
    enabled: visible,
  });

  if (!props.collection) {
    return null;
  }

  const { name } = props.collection;

  return (
    <div
      className={classes(collectionThumbCircleContainer, props.className)}
      onClick={() => props.onClick()}
    >
      <div ref={wrapperRef} className={collectionThumbCircleWrapper}>
          <div
            className={collectionThumbCircle}
            style={
              props.backgroudImageUrl
                ? { backgroundImage: `url(${props.backgroudImageUrl})` }
                : undefined
            }
          >
            {!props.backgroudImageUrl ? (
              <div className={circleFallback}>
                <i
                  className="material-icons notranslate"
                  style={{
                    fontSize: 56, // match the circle size 
                    color: '#bbb',
                    display: 'block',
                    lineHeight: '56px', // match the circle height for vertical centering
                    textAlign: 'center',
                    width: '100%',
                    height: '100%',
                  }}
                >
                  collections
                </i>
              </div>
            ) : null}
          </div>
        </div>
      <div className={circleTextContainer}>
        <div className={circleNameClass}>{StringHelper.upperFirst(name)}</div>
        <div className={circleCountClass}>
          {typeof contentNumber !== 'undefined' ? (
            <NumberFormat value={contentNumber} displayType='text' thousandSeparator />
          ) : (
            <Loader size={10} />
          )}{' '}
          UGC
        </div>
      </div>
    </div>
  );
}

const lang = LocalizedStrings({
  en: {
    errors: {
      usedAsWidgetSource:
        'This collection is used as Widget source. Please remove it from all widgets before deleting this collection',
      unknown: 'An error ocurred',
    },
  },
});

const collectionThumbCircleContainer = style({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  cursor: 'pointer',
  width: 100,
  margin: '0 8px', 
});
const collectionThumbCircleWrapper = style({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 63, // 90 * 0.7 ≈ 63
  height: 63,
  marginBottom: 6, // 8 * 0.7 ≈ 6
});
const collectionThumbCircle = style({
  width: 56, // 80 * 0.7 ≈ 56
  height: 56,
  borderRadius: '50%',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundColor: '#f2f2f2',
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
});
const circleFallback = style({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});
const circleTextContainer = style({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
});
const circleNameClass = style({
  fontSize: 11,
  fontWeight: 500,
  textAlign: 'center',
  maxHeight: '2.6em',
  overflow: 'hidden',
  display: '-webkit-box',
  textOverflow: 'ellipsis',
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  '-webkit-line-clamp': 2,
  WebkitBoxOrient: 'vertical',
  maxWidth: 100, // match container width
  lineHeight: 1.3,
});
const circleCountClass = style({
  fontSize: 8.5,
  opacity: 0.7,
  textAlign: 'center',
  marginTop: 1.5,
});
